import React, { useState, useEffect } from 'react';

/**
 * DatePickerInput - Date picker input field
 * 
 * @param {Object} props
 * @param {Object} props.feature - Feature object from RuleEngine
 * @param {string} props.value - Current date value (ISO format or any parseable date string)
 * @param {Function} props.onChange - Change handler
 * @param {string} props.format - Date format (e.g., 'YYYY-MM-DD', 'MM/DD/YYYY')
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.className - Additional CSS classes
 */
const DatePickerInput = ({ 
    feature, 
    value, 
    onChange,
    format = 'YYYY-MM-DD',
    disabled = false, 
    className = ''
}) => {
    const [error, setError] = useState('');
    const [touched, setTouched] = useState(false);
    const displayText = feature?.property?.displayText || feature?.name || 'Date';
    
    // Parse value to Date object for display
    const dateValue = value ? new Date(value) : null;
    const isValidDate = dateValue && !isNaN(dateValue.getTime());
    
    // Format date for display based on format parameter
    const formatDateForDisplay = (date) => {
        if (!date || isNaN(date.getTime())) return '';
        
        const pad = (num) => num.toString().padStart(2, '0');
        
        switch (format.toUpperCase()) {
            case 'YYYY-MM-DD':
                return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
            case 'MM/DD/YYYY':
                return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`;
            case 'DD/MM/YYYY':
                return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
            case 'MMMM D, YYYY':
                return date.toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                });
            default:
                // ISO format
                return date.toISOString().split('T')[0];
        }
    };
    
    const formattedValue = isValidDate ? formatDateForDisplay(dateValue) : '';
    
    const handleChange = (e) => {
        const inputValue = e.target.value;
        onChange({ target: { value: inputValue } });
        
        // Simple validation - just check if it's non-empty for now
        // More sophisticated validation happens on blur
        if (touched && inputValue) {
            validateValue(inputValue);
        }
    };
    
    const handleBlur = (e) => {
        setTouched(true);
        validateValue(e.target.value);
    };
    
    const validateValue = (val) => {
        if (!val) {
            setError('');
            return;
        }
        
        // Try to parse the date according to the format
        let parsedDate;
        try {
            // Try ISO format first
            if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
                parsedDate = new Date(val);
            } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
                // MM/DD/YYYY or DD/MM/YYYY - try both
                const parts = val.split('/');
                // Try MM/DD/YYYY
                parsedDate = new Date(`${parts[2]}-${parts[0]}-${parts[1]}`);
                if (isNaN(parsedDate.getTime())) {
                    // Try DD/MM/YYYY
                    parsedDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                }
            } else {
                parsedDate = new Date(val);
            }
            
            if (isNaN(parsedDate.getTime())) {
                setError('Invalid date format');
            } else {
                setError('');
            }
        } catch (e) {
            setError('Invalid date');
        }
    };
    
    return (
        <div 
            className="datepicker-input-wrapper"
            style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
        >
            <label style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <span>{displayText}</span>
                {feature?.property?.isOptional !== false && (
                    <span style={{ color: 'var(--error)', fontSize: '0.75rem' }}>*</span>
                )}
            </label>
            <input
                className={className}
                type="date"
                value={formattedValue}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={disabled}
                style={{
                    border: `1px solid ${error ? 'var(--error)' : 'var(--border)'}`,
                    outline: 'none',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : 1
                }}
                disabled={disabled}
            />
            {error && touched && (
                <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--error)',
                    minHeight: '1rem'
                }}>
                    {error}
                </span>
            )}
            {feature?.property?.description && !error && (
                <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    fontStyle: 'italic'
                }}>
                    {feature.property.description}
                </span>
            )}
            {!error && touched && isValidDate && (
                <span style={{
                    fontSize: '0.7rem',
                    color: 'var(--text-secondary)',
                    fontStyle: 'italic'
                }}>
                    Format: {format}
                </span>
            )}
        </div>
    );
};

export default DatePickerInput;
